/**
 * An infuser's values, each as a picture: its hopper as a bar, the blocks in it as chips; the block
 * under way as a bar filling until it lands, or why it lays nothing; and its ring as a little map
 * in its shape on the planet, the tiles that take a block lit, the one its next block goes to
 * brightest.
 */
import type { Block } from '@bramvasterd/terrella-rules';
import type { JSX } from 'react';

import type { InfuserInfo } from '@game/planet/machines';
import type { ReachTile } from '@game/planet/reach';
import { duration, resourceName, t } from '@i18n/index';
import { Icon, ResourceIcon } from '@ui/icons';

import { HoldsBar } from './HoldsBar';
import { ReachMap } from './ReachMap';

export function InfuserStats({
  info,
  ring,
  tile,
}: {
  info: InfuserInfo;
  ring: readonly ReachTile[];
  tile: number;
}): JSX.Element {
  // the blocks in its hopper by kind, in the order the first of each went in
  const counts = new Map<Block, number>();
  for (const block of info.hopper) counts.set(block, (counts.get(block) ?? 0) + 1);
  const { lay } = info;
  const progress = lay && lay.ms > 0 ? Math.round((1 - Math.min(1, lay.left / lay.ms)) * 100) : 0;
  const takes = ring.filter((r) => r.role === 'ground' || r.role === 'next').length;
  return (
    <div className="xstats">
      <HoldsBar stored={info.hopper.length} storage={info.capacity} resource={t('info.blocks')}>
        {counts.size > 0 && (
          <ul className="block-chips" aria-label={t('info.hopper')}>
            {[...counts].map(([block, n]) => (
              <li key={block} data-block={block}>
                <ResourceIcon kind={block} />
                <b>{n}</b>
                <small>{resourceName(block, n).toLowerCase()}</small>
              </li>
            ))}
          </ul>
        )}
      </HoldsBar>
      <div className="holds" data-stat="laying" data-idle={info.idle ?? undefined}>
        <div className="stat-head">
          <span className="k">{t('info.laying')}</span>
          {lay && (
            <small className="time">
              <Icon id="clock" />
              {t('upgrade.left', { time: duration(lay.left) })}
            </small>
          )}
        </div>
        {lay ? (
          <>
            <div
              className="upgrade-bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progress}
              aria-label={t('info.laying')}
            >
              <i className="fill" style={{ width: `${String(Math.max(3, progress))}%` }} />
            </div>
            <small>{t('info.lay-what', { block: resourceName(lay.block) })}</small>
          </>
        ) : (
          <small className="idle">
            {t(info.idle === 'nowhere' ? 'info.idle-nowhere' : 'info.idle-empty')}
          </small>
        )}
      </div>
      <div className="reach wide" data-stat="ring">
        <span className="k">{t('info.ring')}</span>
        <ReachMap tiles={ring} self={tile} />
        <b>{takes}</b>
        <small>{t('info.ring-takes', { n: takes })}</small>
      </div>
    </div>
  );
}
